import {
  Alert,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardTitle,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  Skeleton,
} from '@design-system';
import { PageHeader } from '@/features/quote-and-apply-builder';
import { CloneTemplatesDialog } from '@/features/quote-and-apply-builder/components/clone-templates-dialog';
import { EditFormConfigDialog } from '@/features/quote-and-apply-builder/components/edit-form-config-dialog';
import { useCarrierFormsList } from '@/features/quote-and-apply-builder/hooks/use-carrier-forms-list';
import { useCloneTemplates } from '@/features/quote-and-apply-builder/hooks/use-clone-templates';
import { useEditFormConfig } from '@/features/quote-and-apply-builder/hooks/use-edit-form-config';

import {
  ArrowLeft,
  Clock,
  Copy,
  FileText,
  InfoIcon,
  Layers,
  MapPin,
  Package,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';

const FORM_TYPE_LABELS: Record<string, string> = {
  'application-form': 'Application',
  'pre-underwriting-form': 'Pre-Underwriting',
  'partner-pre-underwriting-form': 'Partner Pre-Underwriting',
};

export function CarrierFormsList() {
  const {
    carrier,
    carrierId,
    forms,
    isLoading,
    error,
    isDeleting,
    formToDelete,
    isEmptyState,
    isDataLoaded,
    handleDeleteClick,
    handleConfirmDelete,
    handleDismissDelete,
    navigateBack,
    navigateToNewForm,
    navigateToForm,
    navigateToCompositeTemplates,
  } = useCarrierFormsList();

  const editFormConfig = useEditFormConfig(carrierId ?? '');
  const cloneTemplates = useCloneTemplates(forms);

  return (
    <div className="mx-auto max-w-5xl px-6 pb-12 pt-6">
      <PageHeader
        title={carrier?.carrierName ?? 'Carrier Forms'}
        description={
          carrier
            ? `${carrier.totalForms} ${carrier.totalForms === 1 ? 'form' : 'forms'} across ${carrier.totalStates} ${carrier.totalStates === 1 ? 'state' : 'states'}`
            : undefined
        }
      >
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={navigateBack}
          >
            <ArrowLeft />
            Back
          </Button>
          <Button onClick={navigateToNewForm}>
            <Plus />
            Create New Form
          </Button>
        </div>
      </PageHeader>

      {isLoading && (
        <div className="flex flex-col gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card
              key={index}
              className="block p-4"
            >
              <div className="flex items-center gap-4">
                <Skeleton className="size-12 rounded-md" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-48" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <InfoIcon />
          <AlertTitle>Failed to load forms. Please try again.</AlertTitle>
        </Alert>
      )}

      {isEmptyState && (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FileText />
            </EmptyMedia>
            <EmptyDescription>
              No forms found{carrier ? ` for ${carrier.carrierName}` : ''}.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button
              size="sm"
              onClick={navigateToNewForm}
            >
              <Plus />
              Create First Form
            </Button>
          </EmptyContent>
        </Empty>
      )}

      {isDataLoaded && (
        <div className="flex flex-col gap-4">
          {forms?.map(form => (
            <Card
              key={form.id}
              onClick={() => navigateToForm(form.id)}
              className="hover:border-primary group relative cursor-pointer p-4 transition-all hover:shadow-none"
            >
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 group-hover:bg-primary text-primary group-hover:text-primary-foreground flex size-14 shrink-0 items-center justify-center rounded-md transition-colors">
                  <FileText className="size-6" />
                </div>
                <div className="mt-1 flex flex-col gap-3">
                  <CardTitle>{form.displayName}</CardTitle>
                  <div className="text-foreground flex flex-wrap items-center gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      <Package className="size-3.5" />
                      {form.product}
                    </span>
                    {form.stateCode && (
                      <span className="flex items-center gap-1">
                        <MapPin className="size-3.5" />
                        {form.stateCode.trim()}
                      </span>
                    )}
                    <Badge variant="secondary">
                      {FORM_TYPE_LABELS[form.formType] || form.formType}
                    </Badge>
                    {form.updatedAt && (
                      <p className="flex items-center gap-1 text-sm">
                        <Clock className="size-3.5" />
                        Updated{' '}
                        {new Date(form.updatedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="absolute right-2 top-2 flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={event => {
                    event.stopPropagation();
                    navigateToCompositeTemplates(form.id);
                  }}
                  title="Composite templates"
                >
                  <Layers className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={event => cloneTemplates.openCloneDialog(event, form)}
                  title="Clone templates to another form"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={event => editFormConfig.handleEditClick(event, form)}
                  title="Edit form settings"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={event => handleDeleteClick(event, form)}
                  title="Delete form"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <EditFormConfigDialog {...editFormConfig} />

      <CloneTemplatesDialog {...cloneTemplates} />

      <AlertDialog
        open={!!formToDelete}
        onOpenChange={handleDismissDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Form</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{formToDelete?.displayName}&quot;? This will
              permanently remove the form and all of its sections, questions, and configuration.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
